// src/features/purchasing2/components/ReceivePanel.jsx
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Purchasing2Api } from '../api';

export function ReceivePanel({ purchaseOrders, bootstrapData, onPOsUpdated }) {
  const [receivingPOs, setReceivingPOs] = useState(new Set());
  const queryClient = useQueryClient();

  // Receive PO mutation
  const receivePOMutation = useMutation(
    ({ poId, items }) => Purchasing2Api.receive(poId, items),
    {
      onMutate: (poId) => {
        setReceivingPOs(prev => new Set(prev).add(poId));
      },
      onSuccess: () => {
        queryClient.invalidateQueries('purchasing2-bootstrap');
      },
      onSettled: (_, __, poId) => {
        setReceivingPOs(prev => {
          const newSet = new Set(prev);
          newSet.delete(poId);
          return newSet;
        });
      },
    }
  );

  const handleReceivePO = async (poId) => {
    try {
      // For now, just mark all items as received
      const items = [{ poItemId: poId, quantity: 1 }]; // Mock data
      await receivePOMutation.mutateAsync({ poId, items });
    } catch (error) {
      console.error('Error receiving PO:', error);
      alert('Failed to receive purchase order');
    }
  };

  const sentPOs = purchaseOrders.filter(po => 
    ['SENT', 'PARTIALLY_RECEIVED'].includes(po.status)
  );

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Receive Items</h2>
      
      {/* Sent Orders */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Sent Orders ({sentPOs.length})
        </h3>
        
        {sentPOs.length > 0 ? (
          <div className="space-y-2">
            {sentPOs.map((po) => (
              <div
                key={po.id}
                className="p-3 border border-gray-200 rounded-md"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">{po.po}</span>
                    <span className="text-sm text-gray-500 ml-2">- {po.vendorName || po.vendor}</span>
                    <div className="text-xs text-gray-500 mt-1">
                      Status: {po.status} • {po.items.length} items
                    </div>
                  </div>
                  <button
                    onClick={() => handleReceivePO(po.id)}
                    disabled={receivingPOs.has(po.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {receivingPOs.has(po.id) ? 'Receiving...' : 'Receive'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">No orders to receive</div>
        )}
      </div>

      {/* Status Messages */}
      {receivePOMutation.isError && (
        <div className="mt-4 text-sm text-red-600">
          Failed to receive purchase order
        </div>
      )}
      {receivePOMutation.isSuccess && (
        <div className="mt-4 text-sm text-green-600">
          Purchase order received successfully!
        </div>
      )}
    </div>
  );
}
